<template>
  <div class="ss">
    <div v-if="!node" class="ss__empty">Select node in widget config</div>
    <template v-else>
      <div class="ss__head">
        <span class="ss__online" :class="node.online ? 'is-on' : 'is-off'">
          <i class="fas fa-circle"></i> {{ node.online ? 'Online' : 'Offline' }}
        </span>
      </div>

      <div v-if="!stats" class="ss__empty">No stats yet</div>
      <div v-else class="ss__body">
        <div v-if="percentMetrics.length" class="ss__donuts">
          <div v-for="m in percentMetrics" :key="m.key" class="ss__donut-card">
            <div class="ss__donut-wrap">
              <svg class="ss__donut" viewBox="0 0 36 36">
                <circle class="ss__donut-bg" cx="18" cy="18" r="15.5" />
                <circle
                  class="ss__donut-fg"
                  cx="18"
                  cy="18"
                  r="15.5"
                  :stroke="m.color"
                  :stroke-dasharray="donutDash(m.pct)"
                />
              </svg>
              <div class="ss__donut-center">
                <strong>{{ m.short }}</strong>
              </div>
            </div>
            <div class="ss__donut-meta">
              <span class="ss__label">{{ m.label }}</span>
              <span v-if="m.sub" class="ss__sub">{{ m.sub }}</span>
              <svg
                v-if="showCharts && m.points.length > 1"
                class="ss__spark"
                viewBox="0 0 100 20"
                preserveAspectRatio="none"
              >
                <polygon :fill="m.fill" :points="m.area" />
                <polyline
                  fill="none"
                  :stroke="m.color"
                  stroke-width="2"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                  :points="m.line"
                />
              </svg>
            </div>
          </div>
        </div>

        <div v-if="rateMetrics.length" class="ss__rates">
          <div v-for="m in rateMetrics" :key="m.key" class="ss__rate">
            <div class="ss__rate-top">
              <span class="ss__label">
                <i class="fas" :class="m.icon"></i> {{ m.label }}
              </span>
              <strong class="ss__value">{{ m.display }}</strong>
            </div>
            <svg
              v-if="showCharts && m.points.length > 1"
              class="ss__spark ss__spark--wide"
              viewBox="0 0 100 20"
              preserveAspectRatio="none"
            >
              <polygon :fill="m.fill" :points="m.area" />
              <polyline
                fill="none"
                :stroke="m.color"
                stroke-width="2"
                stroke-linejoin="round"
                stroke-linecap="round"
                :points="m.line"
              />
            </svg>
          </div>
        </div>

        <div v-if="showTimestamp && stats.timestamp" class="ss__meta is-muted">
          {{ formatTs(stats.timestamp) }}
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const METRIC_DEFS = {
  cpu: {
    label: 'CPU',
    kind: 'percent',
    color: '#22c6ab',
    fill: 'rgba(34, 198, 171, 0.28)',
    value: (s) => (s.cpuPercent != null ? Number(s.cpuPercent) : null),
    pct: (s) => (s.cpuPercent != null ? Number(s.cpuPercent) : 0),
    short: (s) => (s.cpuPercent != null ? `${Math.round(Number(s.cpuPercent))}%` : '—'),
    sub: () => '',
    display: (s) => (s.cpuPercent != null ? `${Number(s.cpuPercent).toFixed(1)}%` : '—'),
  },
  memory: {
    label: 'Mem',
    kind: 'percent',
    color: '#4798e8',
    fill: 'rgba(71, 152, 232, 0.28)',
    value: (s) => (s.memPercent != null ? Number(s.memPercent) : null),
    pct: (s) => (s.memPercent != null ? Number(s.memPercent) : 0),
    short: (s) => (s.memPercent != null ? `${Math.round(Number(s.memPercent))}%` : '—'),
    sub: (s) => (s.memUsedMB != null ? fmtMem(s.memUsedMB) : ''),
    display: (s) => {
      if (s.memPercent != null && s.memUsedMB != null) {
        return `${Number(s.memPercent).toFixed(1)}% · ${fmtMem(s.memUsedMB)}`
      }
      return '—'
    },
  },
  uplink: {
    label: 'Up',
    kind: 'rate',
    icon: 'fa-arrow-up',
    color: '#26c6da',
    fill: 'rgba(38, 198, 218, 0.28)',
    value: (s) => (s.netUplinkBps != null ? Number(s.netUplinkBps) : null),
    display: (s) => (s.netUplinkBps != null ? fmtRate(s.netUplinkBps) : '—'),
  },
  downlink: {
    label: 'Down',
    kind: 'rate',
    icon: 'fa-arrow-down',
    color: '#ab47bc',
    fill: 'rgba(171, 71, 188, 0.28)',
    value: (s) => (s.netDownlinkBps != null ? Number(s.netDownlinkBps) : null),
    display: (s) => (s.netDownlinkBps != null ? fmtRate(s.netDownlinkBps) : '—'),
  },
  goroutines: {
    label: 'Go',
    kind: 'rate',
    icon: 'fa-code-branch',
    color: '#ffbc34',
    fill: 'rgba(255, 188, 52, 0.28)',
    value: (s) => (s.goroutines != null ? Number(s.goroutines) : null),
    display: (s) => (s.goroutines != null ? String(s.goroutines) : '—'),
  },
}

const DEFAULT_METRICS = ['cpu', 'memory', 'uplink', 'downlink']
const MAX_HISTORY = 40
const DONUT_C = 2 * Math.PI * 15.5

function fmtMem(mb) {
  const n = Number(mb)
  if (!Number.isFinite(n)) return '—'
  if (n >= 1024) return `${(n / 1024).toFixed(1)}G`
  return `${Math.round(n)}M`
}

function fmtRate(bps) {
  const n = Number(bps)
  if (!Number.isFinite(n) || n < 0) return '—'
  if (n < 1024) return `${n.toFixed(0)}B/s`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}K/s`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)}M/s`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)}G/s`
}

function sparkPoints(values) {
  const nums = values.map((v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v)))
  const known = nums.filter((v) => v != null)
  if (known.length < 2) return { line: '', area: '', points: known }

  let min = Math.min(...known)
  let max = Math.max(...known)
  if (min === max) {
    min = Math.max(0, min - 1)
    max = max + 1
  }
  const pad = (max - min) * 0.1
  min -= pad
  max += pad

  const n = nums.length
  const coords = nums.map((v, i) => {
    const x = n === 1 ? 0 : (i / (n - 1)) * 100
    const val = v == null ? known[known.length - 1] : v
    const y = 18 - ((val - min) / (max - min)) * 16
    return { x, y }
  })
  const line = coords.map((c) => `${c.x.toFixed(2)},${c.y.toFixed(2)}`).join(' ')
  const area = `0,20 ${line} 100,20`
  return { line, area, points: known }
}

export default {
  props: { config: { type: Object, default: () => ({}) } },
  data() {
    return { historyByNode: {} }
  },
  computed: {
    ...mapGetters('mission', ['nodeById']),
    node() {
      return this.config.nodeId ? this.nodeById(this.config.nodeId) : null
    },
    stats() {
      if (!this.node?.system_stats) return null
      try {
        return typeof this.node.system_stats === 'string'
          ? JSON.parse(this.node.system_stats)
          : this.node.system_stats
      } catch (e) {
        return null
      }
    },
    enabledMetrics() {
      const list = Array.isArray(this.config.metrics) && this.config.metrics.length
        ? this.config.metrics
        : DEFAULT_METRICS
      return list.filter((k) => METRIC_DEFS[k])
    },
    showCharts() {
      return this.config.showCharts !== false
    },
    showTimestamp() {
      return this.config.showTimestamp === true
    },
    historyLimit() {
      const n = Number(this.config.historyPoints)
      if (!Number.isFinite(n)) return 20
      return Math.min(MAX_HISTORY, Math.max(5, Math.round(n)))
    },
    history() {
      const id = this.config.nodeId
      return (id && this.historyByNode[id]) || []
    },
    enriched() {
      return this.enabledMetrics.map((key) => {
        const def = METRIC_DEFS[key]
        const spark = sparkPoints(this.history.map((p) => p[key]))
        const s = this.stats
        return {
          key,
          kind: def.kind,
          label: def.label,
          icon: def.icon || 'fa-circle',
          color: def.color,
          fill: def.fill,
          display: s ? def.display(s) : '—',
          short: s && def.short ? def.short(s) : '—',
          sub: s && def.sub ? def.sub(s) : '',
          pct: s && def.pct ? Math.max(0, Math.min(100, def.pct(s))) : 0,
          line: spark.line,
          area: spark.area,
          points: spark.points,
        }
      })
    },
    percentMetrics() {
      return this.enriched.filter((m) => m.kind === 'percent')
    },
    rateMetrics() {
      return this.enriched.filter((m) => m.kind !== 'percent')
    },
  },
  watch: {
    stats: {
      immediate: true,
      handler(s) {
        if (!s || !this.config.nodeId) return
        this.pushHistory(this.config.nodeId, s)
      },
    },
  },
  methods: {
    donutDash(pct) {
      const filled = (DONUT_C * pct) / 100
      return `${filled} ${DONUT_C}`
    },
    formatTs(ts) {
      try {
        return new Date(ts).toLocaleTimeString()
      } catch (e) {
        return ts
      }
    },
    pushHistory(nodeId, stats) {
      const prev = this.historyByNode[nodeId] || []
      const last = prev[prev.length - 1]
      if (last && last.timestamp && stats.timestamp && last.timestamp === stats.timestamp) return

      const point = { timestamp: stats.timestamp || new Date().toISOString() }
      Object.keys(METRIC_DEFS).forEach((key) => {
        point[key] = METRIC_DEFS[key].value(stats)
      })

      const next = [...prev, point]
      while (next.length > this.historyLimit) next.shift()
      this.$set(this.historyByNode, nodeId, next)
    },
  },
}
</script>

<style scoped>
.ss {
  color: var(--ws-text);
  display: flex;
  flex-direction: column;
  gap: 0.35em;
  height: 100%;
  min-height: 0;
  overflow: auto;
  container-type: inline-size;
}
.ss__empty {
  font-size: 0.75em;
  color: var(--ws-text-muted, #6c757d);
}
.ss__empty {
  font-size: 0.75em;
  color: var(--ws-text-muted, #6c757d);
}
.ss__head {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.35em;
  flex-shrink: 0;
  min-width: 0;
}
.ss__name {
  font-size: 0.68em;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}
.ss__online {
  font-size: 0.62em;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}
.ss__online i {
  font-size: 0.4em;
  vertical-align: middle;
  margin-right: 0.15rem;
}
.ss__online.is-on { color: var(--wc-success, #22c6ab); }
.ss__online.is-off { color: var(--wc-danger, #ef6e6e); }

.ss__body {
  display: flex;
  flex-direction: column;
  gap: 0.35em;
  min-height: 0;
  flex: 1;
}

.ss__donuts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.3em;
}
.ss__donut-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.15em;
  min-width: 0;
  text-align: center;
}
.ss__donut-wrap {
  position: relative;
  width: 2.55em;
  height: 2.55em;
  flex-shrink: 0;
}
.ss__donut {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  display: block;
}
.ss__donut-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.08);
  stroke-width: 3.5;
}
.ss__donut-fg {
  fill: none;
  stroke-width: 3.5;
  stroke-linecap: round;
  transition: stroke-dasharray 0.35s ease;
}
.ss__donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.48em;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.ss__donut-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.05em;
  min-width: 0;
  width: 100%;
  flex-shrink: 0;
}
.ss__sub {
  font-size: 0.58em;
  color: var(--ws-text-muted, #6c757d);
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.ss__rates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25em 0.35em;
}
.ss__rate {
  min-width: 0;
}
.ss__rate-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.25rem;
}
.ss__label {
  font-size: 0.58em;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--ws-text-muted, #6c757d);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ss__label i {
  font-size: 0.55em;
  margin-right: 0.1rem;
}
.ss__value {
  font-size: 0.65em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.ss__spark {
  display: block;
  width: 100%;
  height: 22px;
  min-height: 18px;
  flex-shrink: 0;
  overflow: visible;
}
.ss__spark--wide {
  margin-top: 0.08em;
}

.ss__meta {
  font-size: 0.65em;
  line-height: 1.2;
  color: var(--ws-text-muted, #6c757d);
  flex-shrink: 0;
}
.ss__meta.is-muted {
  opacity: 0.85;
}

@container (max-width: 220px) {
  .ss__donut-wrap {
    width: 2.35em;
    height: 2.35em;
  }
  .ss__donut-center {
    font-size: 0.44em;
  }
}

@container (max-width: 150px) {
  .ss__donuts,
  .ss__rates {
    grid-template-columns: 1fr;
  }
}

@container (max-height: 120px) {
  .ss__rates {
    display: none;
  }
}
</style>
